import React, { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw, X } from "lucide-react";

/* ── "RockLink has been updated" (5-Nov-2026, Tushar) ─────────────────────────
   An open tab keeps running the code it loaded until someone presses F5, so
   after a Deploy most people stayed on the old version for hours or days.

   How it works (no backend needed):
   · The production build names its main script by content hash
     (/static/js/main.<hash>.js). We read the hash this tab is running, then
     every 5 minutes, on tab focus and when the network comes back, fetch
     /index.html fresh and compare. A different hash = a Deploy happened.
   · Then a corner card offers Refresh. It also refreshes BY ITSELF, but only
     when that can't lose anything: the tab is in the background or nobody has
     touched it for 10 minutes, AND no dialog/menu is open, nobody is typing,
     no edited form field is still on screen, and they're not on Analyst.
   · Before any reload we check the new script really answers 200, so a
     reload never lands on a half-finished Deploy.
   · "Later" hides the card for 2 hours; the safe auto-refresh still runs.
   The Emergent preview runs a dev server (no hashes), so the check is dormant
   there. For local testing, development builds read two localStorage keys:
   rl_update_sim_current / rl_update_sim_latest (any two different strings). */

const CHECK_EVERY_MS = 5 * 60 * 1000;
const MIN_GAP_MS = 60 * 1000;            // focus/online checks at most once a minute
const IDLE_MS = 10 * 60 * 1000;
const LATER_MS = 2 * 60 * 60 * 1000;
const SAFE_POLL_MS = 30 * 1000;
const DEV = process.env.NODE_ENV !== "production";

// Corner cards that sit in the same spot and never hold unsaved work.
const NON_BLOCKING = '[data-testid="mention-notif-banner"]';

const HASH_RE = /\/static\/js\/main\.([0-9a-f]{6,})\.js/;

function runningHash() {
  if (DEV) {
    try { const sim = localStorage.getItem("rl_update_sim_current"); if (sim) return sim; } catch {}
  }
  for (const s of document.querySelectorAll("script[src]")) {
    const m = s.getAttribute("src").match(HASH_RE);
    if (m) return m[1];
  }
  return null;
}

async function latestBuild() {
  if (DEV) {
    try {
      const sim = localStorage.getItem("rl_update_sim_latest");
      if (sim) return { hash: sim, src: null };
    } catch {}
  }
  const res = await fetch(`/index.html?v=${Date.now()}`, { cache: "no-store", credentials: "same-origin" });
  if (!res.ok) return null;
  const m = (await res.text()).match(HASH_RE);
  return m ? { hash: m[1], src: m[0] } : null;
}

// The new script must be reachable before we reload into it.
async function buildIsReady(src) {
  if (!src) return true;
  try {
    const res = await fetch(src, { method: "HEAD", cache: "no-store" });
    return res.ok;
  } catch { return false; }
}

// Form fields someone has typed into that are still on screen.
const edited = new Set();
function hasUnsavedInput() {
  for (const el of edited) {
    if (!el.isConnected) { edited.delete(el); continue; }
    if (el.type === "search" || el.type === "password") continue;
    if (el.isContentEditable ? el.textContent.trim() : String(el.value || "").trim()) return true;
  }
  return false;
}

function isSafeToReload() {
  if (window.location.pathname.startsWith("/analyst")) return false;
  // Open dialogs/menus may hold work. Non-modal corner cards that hold none
  // (the one-time "enable desktop alerts" prompt) don't count.
  const open = [...document.querySelectorAll('[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"]')]
    .filter(el => !el.matches(NON_BLOCKING));
  if (open.length) return false;
  const a = document.activeElement;
  if (a && (a.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName))) return false;
  if (hasUnsavedInput()) return false;
  return true;
}

export default function UpdateNotice() {
  const [pending, setPending] = useState(null);          // { hash, src } once a newer build exists
  const [hiddenUntil, setHiddenUntil] = useState(0);
  const [, tick] = useState(0);
  const current = useRef(null);
  const lastCheck = useRef(0);
  const lastActive = useRef(Date.now());
  const reloading = useRef(false);

  const reload = useCallback(async (build) => {
    if (reloading.current) return;
    reloading.current = true;
    if (await buildIsReady(build?.src)) {
      window.location.reload();
    } else {
      reloading.current = false;                        // Deploy still settling; try again later
    }
  }, []);

  const check = useCallback(async (force = false) => {
    if (!current.current) return;
    const now = Date.now();
    if (!force && now - lastCheck.current < MIN_GAP_MS) return;
    lastCheck.current = now;
    try {
      const latest = await latestBuild();
      if (latest && latest.hash !== current.current) setPending(latest);
    } catch { /* offline or mid-Deploy: try next time */ }
  }, []);

  // Start checking (production builds only; dev only with the sim keys).
  useEffect(() => {
    current.current = runningHash();
    if (!current.current) return undefined;
    const first = setTimeout(() => check(true), 60 * 1000);
    const every = setInterval(() => check(true), CHECK_EVERY_MS);
    const onVisible = () => { if (!document.hidden) check(); };
    window.addEventListener("focus", onVisible);
    window.addEventListener("online", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    if (DEV) window.__rlCheckForUpdate = () => check(true);
    if (DEV) window.__rlPretendIdle = () => { lastActive.current = 0; };
    return () => {
      clearTimeout(first); clearInterval(every);
      window.removeEventListener("focus", onVisible);
      window.removeEventListener("online", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [check]);

  // Activity + typed-into fields, for the safety rules.
  useEffect(() => {
    const active = () => { lastActive.current = Date.now(); };
    const typed = (e) => {
      const t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA)$/.test(t.tagName))) edited.add(t);
    };
    const opts = { passive: true, capture: true };
    ["pointerdown", "keydown", "wheel", "touchstart"].forEach(ev => window.addEventListener(ev, active, opts));
    document.addEventListener("input", typed, true);
    return () => {
      ["pointerdown", "keydown", "wheel", "touchstart"].forEach(ev => window.removeEventListener(ev, active, opts));
      document.removeEventListener("input", typed, true);
    };
  }, []);

  // Once an update is waiting: refresh by itself when that's safe.
  useEffect(() => {
    if (!pending) return undefined;
    const tryAuto = () => {
      const away = document.hidden || Date.now() - lastActive.current > IDLE_MS;
      if (away && isSafeToReload()) reload(pending);
    };
    const t = setInterval(tryAuto, SAFE_POLL_MS);
    document.addEventListener("visibilitychange", tryAuto);
    tryAuto();
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", tryAuto); };
  }, [pending, reload]);

  // While the card shows, re-check its neighbour now and then (cheap).
  useEffect(() => {
    if (!pending) return undefined;
    const t = setInterval(() => tick(n => n + 1), 2000);
    return () => clearInterval(t);
  }, [pending]);

  // Re-show the card when "Later" runs out.
  useEffect(() => {
    if (!hiddenUntil) return undefined;
    const t = setTimeout(() => tick(n => n + 1), Math.max(0, hiddenUntil - Date.now()) + 50);
    return () => clearTimeout(t);
  }, [hiddenUntil]);

  if (!pending || Date.now() < hiddenUntil) return null;

  // Sit above the "enable desktop alerts" card if that's showing in the corner.
  const neighbour = document.querySelector(NON_BLOCKING);
  const lift = neighbour ? neighbour.getBoundingClientRect().height + 12 : 0;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="update-notice"
      style={lift ? { bottom: 16 + lift } : undefined}
      className="fixed bottom-4 right-4 z-[110] w-[min(320px,calc(100vw-2rem))] rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3.5 py-3 flex items-start gap-3 shadow-[0_10px_30px_-12px_rgba(0,0,0,0.35)] rl-update-in"
    >
      <span className="mt-0.5 w-8 h-8 shrink-0 rounded-lg bg-orange-500/15 text-orange-500 flex items-center justify-center" aria-hidden="true">
        <RefreshCw className="w-4 h-4" strokeWidth={2.2} />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-semibold text-[hsl(var(--foreground))]">RockLink has been updated</p>
        <p className="mt-0.5 text-[11.5px] leading-snug text-[hsl(var(--muted-foreground))]">
          Refresh to get the new version. It refreshes by itself when you step away.
        </p>
        <div className="mt-2.5 flex items-center gap-2">
          <button
            type="button"
            onClick={() => reload(pending)}
            data-testid="update-notice-refresh"
            className="h-7 px-3 rounded-md bg-orange-500 hover:bg-orange-600 text-white text-[12px] font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50"
          >
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setHiddenUntil(Date.now() + LATER_MS)}
            data-testid="update-notice-later"
            className="h-7 px-2 rounded-md text-[12px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
          >
            Later
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={() => setHiddenUntil(Date.now() + LATER_MS)}
        aria-label="Hide for now"
        className="-mr-1 -mt-1 p-1 rounded text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
